import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { DrillSpec } from '@4cm/core/drills/types';
import { supabase } from '../lib/supabase';
import { DrillMiniPreview } from '../components/drillCanvas/DrillMiniPreview';
import { synthesizeDrillSpec } from '../components/drillCanvas/synthesizeDrillSpec';

import { DrillCanvasEditor } from '../components/DrillCanvasEditor';
import { ReviewFeedbackCard } from '../components/ReviewFeedbackCard';
import { Button, Card, GlassPill, Mic } from '../components/primitives';
import { useVoiceToText } from '../lib/voiceInput';
import './page-shell.css';

interface DrillRow {
  id: string;
  name: string | null;
  description: string | null;
  drill_type: string | null;
  duration_mins: number | null;
  min_players: number | null;
  max_players: number | null;
  space_required: string | null;
  equipment: string[] | null;
  coaching_points: string[] | null;
  sub_themes: string[] | null;
  source_type: string | null;
  curator_name: string | null;
  drill_spec: DrillSpec | null;
}

/**
 * /drills/:id — full visual + content for a single drill.
 *
 *  Renders the DrillCanvasPreview at full width so the coach can see
 *  the pitch layout, equipment, and movement arrows. Coaching points +
 *  description + equipment list sit beside it.
 *
 *  No editor mode here — that lives at /drills/new?refine=:id behind
 *  the academy gate. This page is read-mostly for reviewers + grassroots.
 */
export function DrillDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const openInEditMode = searchParams.get('edit') === '1';
  const [drill, setDrill] = useState<DrillRow | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error' | 'not_found'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Edit mode state — separate from `drill` so a Cancel reverts cleanly.
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [draftCoachingPoints, setDraftCoachingPoints] = useState<string[]>([]);
  const [draftSpec, setDraftSpec] = useState<DrillSpec | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setState('not_found');
      return;
    }
    let cancelled = false;
    setState('loading');
    void (async () => {
      const { data, error } = await supabase
        .from('drills')
        .select('id, name, description, drill_type, duration_mins, min_players, max_players, space_required, equipment, coaching_points, sub_themes, source_type, curator_name, drill_spec')
        .eq('id', id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setState('error');
        setErrorMsg(error.message);
        return;
      }
      if (!data) {
        setState('not_found');
        return;
      }
      setDrill(data as DrillRow);
      setState('ok');
      // If the URL says ?edit=1 (drill was just created via "+ New
      // drill"), drop the coach straight into edit mode.
      if (openInEditMode) {
        setDraftName(data.name ?? '');
        setDraftDesc(data.description ?? '');
        setDraftCoachingPoints(data.coaching_points ?? []);
        setDraftSpec(data.drill_spec ?? null);
        setEditing(true);
      }
    })();
    return () => { cancelled = true; };
  }, [id, openInEditMode]);

  // Voice dictation for the Description textarea. Mic appends what
  // the coach speaks to whatever's currently in the field. Use the
  // same hook every other surface uses, so behaviour is consistent.
  const lastVoiceLenRef = useRef(0);
  const voice = useVoiceToText({
    onTranscript: (text) => {
      // Append delta (new portion since last call) to draftDesc so a
      // long dictation doesn't overwrite earlier text.
      const delta = text.slice(lastVoiceLenRef.current);
      lastVoiceLenRef.current = text.length;
      if (delta.trim()) {
        setDraftDesc((prev) => (prev ? `${prev} ${delta.trim()}` : delta.trim()));
      }
    },
  });
  useEffect(() => {
    if (!voice.listening) lastVoiceLenRef.current = 0;
  }, [voice.listening]);

  const beginEdit = useCallback(() => {
    if (!drill) return;
    setDraftName(drill.name ?? '');
    setDraftDesc(drill.description ?? '');
    setDraftCoachingPoints(drill.coaching_points ?? []);
    setDraftSpec(drill.drill_spec ?? null);
    setSaveMessage(null);
    setEditing(true);
  }, [drill]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setSaveMessage(null);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!drill || saving) return;
    setSaving(true);
    setSaveMessage(null);
    const patch: Record<string, unknown> = {
      name: draftName.trim() || drill.name,
      description: draftDesc.trim() || null,
      coaching_points: draftCoachingPoints.map((s) => s.trim()).filter((s) => s.length > 0),
    };
    if (draftSpec) patch.drill_spec = draftSpec;
    const { error } = await supabase
      .from('drills')
      .update(patch)
      .eq('id', drill.id);
    setSaving(false);
    if (error) {
      setSaveMessage(`Save failed: ${error.message}`);
      return;
    }
    // Update local state so the view-mode re-render shows the new values.
    setDrill({
      ...drill,
      name: (patch.name as string | null) ?? drill.name,
      description: (patch.description as string | null) ?? null,
      coaching_points: patch.coaching_points as string[],
      drill_spec: draftSpec ?? drill.drill_spec,
    });
    setEditing(false);
    setSaveMessage('Saved.');
    window.setTimeout(() => setSaveMessage(null), 2000);
  }, [drill, draftName, draftDesc, draftCoachingPoints, draftSpec, saving]);

  if (state === 'loading') {
    return (
      <main className="main page">
        <p style={{ opacity: 0.6 }}>Loading drill…</p>
      </main>
    );
  }
  if (state === 'error') {
    return (
      <main className="main page">
        <p style={{ color: 'var(--state-bad)' }}>Couldn't load drill: {errorMsg}</p>
        <Link to="/drills">← Back to drills</Link>
      </main>
    );
  }
  if (state === 'not_found' || !drill) {
    return (
      <main className="main page">
        <p>Drill not found.</p>
        <Link to="/drills">← Back to drills</Link>
      </main>
    );
  }

  // ---------------- EDIT MODE ----------------
  if (editing && drill) {
    return (
      <main className="main page" style={{ maxWidth: 1100, margin: '0 auto', padding: '24px' }}>
        <Link
          to="/drills"
          style={{
            display: 'inline-block',
            fontSize: 13,
            color: '#fff',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 999,
            padding: '4px 12px',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >← Back to drills</Link>
        <header style={{ margin: '12px 0 20px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Editing</span>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            style={{ flex: 1, minWidth: 280, fontSize: 24, padding: '6px 10px', background: 'rgba(255,255,255,0.08)', color: 'inherit', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6 }}
            aria-label="Drill name"
          />
          <Button variant="primary" size="workbench" onClick={() => void saveEdit()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="glass" size="workbench" onClick={cancelEdit} disabled={saving}>
            Cancel
          </Button>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 24, alignItems: 'start' }}>
          {/* LEFT — interactive canvas editor */}
          <Card variant="opaque">
            <div style={{ padding: 12 }}>
              {draftSpec ? (
                <DrillCanvasEditor
                  spec={draftSpec}
                  onChange={(next) => setDraftSpec(next)}
                  width={640}
                />
              ) : (
                <div style={{ padding: 24, textAlign: 'center', opacity: 0.7 }}>
                  No canvas yet — re-redeem your invite to seed one, or open
                  <Link to="/drills/new" style={{ marginLeft: 4 }}>Drill Author with Brain</Link>.
                </div>
              )}
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Description</span>
                <Mic
                  listening={voice.listening}
                  mode="toggle"
                  onStart={() => void voice.start()}
                  onStop={voice.stop}
                  aria-label={voice.listening ? 'Stop dictation' : 'Dictate description'}
                  title={voice.listening ? 'Stop dictating' : 'Dictate into the description'}
                />
              </div>
              <textarea
                value={draftDesc}
                onChange={(e) => setDraftDesc(e.target.value)}
                rows={4}
                style={{ marginTop: 6, width: '100%', fontSize: 14, lineHeight: 1.5, padding: 10, background: 'rgba(255,255,255,0.08)', color: 'inherit', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, resize: 'vertical' }}
              />
              {voice.status && (
                <span style={{ display: 'block', fontSize: 11, opacity: 0.7, marginTop: 4 }}>{voice.status}</span>
              )}
            </div>
          </Card>

          {/* RIGHT — coaching-point editor */}
          <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card variant="opaque">
              <div style={{ padding: 14 }}>
                <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Coaching points
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {draftCoachingPoints.map((cp, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text"
                        value={cp}
                        onChange={(e) => {
                          const next = [...draftCoachingPoints];
                          next[i] = e.target.value;
                          setDraftCoachingPoints(next);
                        }}
                        style={{ flex: 1, fontSize: 13, padding: 6, background: 'rgba(255,255,255,0.08)', color: 'inherit', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4 }}
                      />
                      <button
                        type="button"
                        onClick={() => setDraftCoachingPoints(draftCoachingPoints.filter((_, j) => j !== i))}
                        title="Remove point"
                        style={{ width: 28, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'inherit', borderRadius: 4, cursor: 'pointer' }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setDraftCoachingPoints([...draftCoachingPoints, ''])}
                    style={{ marginTop: 4, padding: '6px 10px', fontSize: 12, background: 'transparent', border: '1px dashed rgba(255,255,255,0.3)', color: 'inherit', borderRadius: 4, cursor: 'pointer' }}
                  >+ Add point</button>
                </div>
              </div>
            </Card>
          </aside>
        </div>
        {saveMessage && (
          <p style={{ marginTop: 12, fontSize: 13, color: saveMessage.startsWith('Save failed') ? 'var(--state-bad)' : 'var(--state-good)' }} aria-live="polite">
            {saveMessage}
          </p>
        )}
      </main>
    );
  }

  // ---------------- VIEW MODE ----------------
  return (
    <main className="main page" style={{ maxWidth: 1100, margin: '0 auto', padding: '24px' }}>
      <Link
        to="/drills"
        style={{
          display: 'inline-block',
          fontSize: 13,
          color: '#fff',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 999,
          padding: '4px 12px',
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >← Back to drills</Link>
      <header style={{ margin: '12px 0 20px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 28, margin: 0, flex: 1 }}>{drill.name ?? 'Untitled drill'}</h1>
        <Link
          to={`/drills/${drill.id}/canvas`}
          style={{ padding: '6px 14px', background: 'var(--brand, #2980B9)', color: '#fff', textDecoration: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14 }}
        >
          🎨 Open drill canvas
        </Link>
        <Button variant="glass" size="workbench" onClick={beginEdit}>
          ✎ Quick edit
        </Button>
      </header>
      <div style={{ display: 'flex', gap: 8, margin: '0 0 20px', flexWrap: 'wrap' }}>
        {drill.drill_type && <GlassPill mono>{drill.drill_type.replace(/_/g, ' ').toLowerCase()}</GlassPill>}
        {drill.duration_mins && <GlassPill mono>{drill.duration_mins} min</GlassPill>}
        {(drill.min_players || drill.max_players) && (
          <GlassPill mono>
            {drill.min_players && drill.max_players
              ? `${drill.min_players}–${drill.max_players} players`
              : `${drill.min_players ?? drill.max_players} players`}
          </GlassPill>
        )}
        {drill.space_required && <GlassPill mono>{drill.space_required}</GlassPill>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 24, alignItems: 'start' }}>
        {/* LEFT — full-size canvas */}
        <Card variant="opaque">
          <div style={{ padding: 12 }}>
            {/* One unified preview. Synthesise a sensible layout when the
                drill has no spec so it never renders empty. */}
            <DrillMiniPreview
              spec={drill.drill_spec ?? synthesizeDrillSpec({
                drillType: drill.drill_type,
                minPlayers: drill.min_players,
                maxPlayers: drill.max_players,
                name: drill.name,
              })}
              width={640}
            />
          </div>
          {drill.description && (
            <p style={{ padding: '0 16px 16px', fontSize: 14, lineHeight: 1.5, opacity: 0.85 }}>
              {drill.description}
            </p>
          )}
        </Card>

        {/* RIGHT — coaching points + equipment + themes */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Pro-coach reviewers (Ben) get a prominent feedback prompt here;
              no-op for everyone else. */}
          <ReviewFeedbackCard entityType="DRILL" entityId={drill.id} entityName={drill.name ?? 'this drill'} />
          {drill.coaching_points && drill.coaching_points.length > 0 && (
            <Card variant="opaque">
              <div style={{ padding: 14 }}>
                <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Coaching points
                </span>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
                  {drill.coaching_points.map((cp, i) => <li key={i}>{cp}</li>)}
                </ul>
              </div>
            </Card>
          )}

          {drill.equipment && drill.equipment.length > 0 && (
            <Card variant="opaque">
              <div style={{ padding: 14 }}>
                <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Equipment
                </span>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
                  {drill.equipment.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            </Card>
          )}

          {drill.sub_themes && drill.sub_themes.length > 0 && (
            <Card variant="opaque">
              <div style={{ padding: 14 }}>
                <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Themes
                </span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {drill.sub_themes.map((t) => (
                    <span key={t} style={{ fontSize: 12, padding: '4px 8px', background: 'rgba(255,255,255,0.08)', borderRadius: 12 }}>{t}</span>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </aside>
      </div>
      {saveMessage && (
        <p style={{ marginTop: 12, fontSize: 13, color: 'var(--state-good)' }} aria-live="polite">{saveMessage}</p>
      )}
    </main>
  );
}
